import React from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Package } from "lucide-react"

import { OrderStatus, PaymentStatus, type Order } from "@/api/api-client"
import Footer from "@/components/Footer"
import Header from "@/components/Header"
import { OrderFilterBar } from "@/components/orders/OrderFilterBar"
import { BuyerOrderCard } from "@/components/orders/tracking/BuyerOrderCard"
import {
  buildCartProductFromOrderItem,
  getLatestPaymentStatus,
  getOrderReorderItems,
  isPaymentAttemptProcessing,
} from "@/components/orders/tracking/buyer-order-utils"
import { useOrderTrackingFilters } from "@/components/orders/tracking/useOrderTrackingFilters"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { EmptyState } from "@/components/ui/empty-state"
import { LoadingState } from "@/components/ui/loading-state"
import { PageHeader } from "@/components/ui/page-header"
import { useCart } from "@/hooks/useCart"
import { useLanguage } from "@/hooks/useLanguage"
import { useCreateSubOrderCheckout, useMyOrders } from "@/hooks/useOrders"
import { loadConektaScript, mountConektaWidget } from "@/lib/conekta"
import { toastError, toastInformation, toastSuccess } from "@/lib/toast"

const OrderTracking = () => {
  const { t } = useLanguage()
  const text = t.order
  const queryClient = useQueryClient()
  const { addItem } = useCart()
  const [checkingOutSubOrderId, setCheckingOutSubOrderId] = React.useState<number | null>(null)
  const [activePayment, setActivePayment] = React.useState<{
    checkoutId: string
    subOrderId: number
  } | null>(null)
  const [awaitingPaymentSubOrderIds, setAwaitingPaymentSubOrderIds] = React.useState<Set<number>>(
    new Set()
  )
  const [expandedPickupIds, setExpandedPickupIds] = React.useState<Set<number>>(new Set())
  const { data: orders, isLoading } = useMyOrders()
  const { mutate: createCheckout, isPending: isCheckingOut } = useCreateSubOrderCheckout()

  const {
    filteredOrders,
    statusFilter,
    setStatusFilter,
    periodFilter,
    setPeriodFilter,
    searchQuery,
    setSearchQuery,
  } = useOrderTrackingFilters({ orders })

  const buyerOrderLabels = {
    ...t.orderTracking,
    statuses: text.statuses,
    orderId: text.orderId,
    pipelineProgress: text.pipelineProgress,
    shippingTitle: text.shippingTitle,
    originShipment: text.originShipment,
  }
  const hasProcessingPayment = React.useMemo(
    () =>
      orders?.some((order) =>
        order.subOrders?.some((subOrder) => isPaymentAttemptProcessing(subOrder))
      ) ?? false,
    [orders]
  )

  const togglePickupAddress = React.useCallback((subOrderId: number) => {
    setExpandedPickupIds((prev) => {
      const next = new Set(prev)
      if (next.has(subOrderId)) {
        next.delete(subOrderId)
        return next
      }
      next.add(subOrderId)
      return next
    })
  }, [])
  const handlePayNow = (subOrderId: number) => {
    if (awaitingPaymentSubOrderIds.has(subOrderId)) return

    setCheckingOutSubOrderId(subOrderId)
    createCheckout(subOrderId, {
      onSuccess: (response) => {
        setCheckingOutSubOrderId(null)
        if (response.id) {
          setActivePayment({ checkoutId: response.id, subOrderId })
        } else {
          toastError(t.orderTracking.paymentLinkUnavailable)
        }
      },
      onError: () => {
        toastError(t.orderTracking.paymentStartError)
        setCheckingOutSubOrderId(null)
      },
    })
  }
  const handleReorder = (order: Order) => {
    const lineItems = getOrderReorderItems(order)
    let addedItems = 0

    lineItems.forEach(({ item, pickupWarehouseId }) => {
      const product = buildCartProductFromOrderItem(item)
      if (!product) return

      addItem(product, item.quantity, pickupWarehouseId)
      addedItems += 1
    })

    if (addedItems === 0) {
      toastError(t.orderTracking.reorderUnavailable, t.orderTracking.reorderUnavailableDescription)
      return
    }

    toastSuccess(t.orderTracking.reorderAdded.replace("{count}", addedItems.toString()))
  }
  const handleDownloadInvoice = () => {
    toastInformation(t.orderTracking.invoiceComingSoon)
  }
  const handleTrackShipment = () => {
    toastInformation(t.orderTracking.shipmentTrackingComingSoon)
  }

  React.useEffect(() => {
    if (!activePayment) return

    let cancelMount: (() => void) | undefined
    let isEffectActive = true

    loadConektaScript()
      .then(() => {
        if (!isEffectActive) return

        cancelMount = mountConektaWidget({
          containerId: "order-tracking-conekta-container",
          checkoutRequestId: activePayment.checkoutId,
          publicKey: import.meta.env.VITE_CONEKTA_PUBLIC_KEY,
          onFinalize: () => {
            setAwaitingPaymentSubOrderIds((prev) => new Set(prev).add(activePayment.subOrderId))
            setActivePayment(null)
            queryClient.invalidateQueries({ queryKey: ["my-orders"] })
            toastSuccess(t.checkout.orderSuccess)
          },
        })
      })
      .catch(() => {
        if (!isEffectActive) return

        toastError(t.orderTracking.paymentSystemLoadError)
      })

    return () => {
      isEffectActive = false
      cancelMount?.()
    }
  }, [
    activePayment,
    queryClient,
    t.checkout.orderSuccess,
    t.orderTracking.paymentSystemLoadError,
  ])

  React.useEffect(() => {
    if (awaitingPaymentSubOrderIds.size === 0 || !orders) return

    setAwaitingPaymentSubOrderIds((prev) => {
      const next = new Set(prev)

      orders
        .flatMap((order) => order.subOrders ?? [])
        .filter((subOrder) => subOrder.id != null && prev.has(subOrder.id))
        .forEach((subOrder) => {
          const paymentStatus = getLatestPaymentStatus(subOrder)
          if (
            subOrder.status !== OrderStatus.ShippingQuoted ||
            paymentStatus === PaymentStatus.Failed ||
            paymentStatus === PaymentStatus.Paid
          ) {
            next.delete(subOrder.id!)
          }
        })

      return next.size === prev.size ? prev : next
    })
  }, [awaitingPaymentSubOrderIds.size, orders])

  React.useEffect(() => {
    if (awaitingPaymentSubOrderIds.size === 0 && !hasProcessingPayment) return

    const intervalId = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["my-orders"] })
    }, 3000)

    return () => window.clearInterval(intervalId)
  }, [awaitingPaymentSubOrderIds.size, hasProcessingPayment, queryClient])

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground transition-colors duration-300">
      <Header />

      <main className="container mx-auto flex-1 px-4 py-12">
        <PageHeader
          title={text.title}
          subtitle={text.subtitle}
          subtitleClassName="tracking-subtitle font-bold"
        >
          {orders && orders.length > 0 && (
            <div className="text-right">
              <p className="label-industrial mb-1">{text.totalOrders}</p>
              <div className="font-display text-4xl tracking-widest">{orders.length}</div>
            </div>
          )}
        </PageHeader>

        {isLoading ? (
          <LoadingState label={text.loading || "Loading..."} />
        ) : !orders || orders.length === 0 ? (
          <EmptyState icon={Package} message={text.empty} />
        ) : (
          <>
            <OrderFilterBar
              className="mx-auto mb-8 max-w-7xl"
              controlsClassName="flex-row"
              periodSelectClassName="sm:w-auto"
              searchClassName="w-48"
              statusValue={statusFilter}
              onStatusChange={setStatusFilter}
              statusOptions={[
                { value: "all", label: t.common.all || "Todos" },
                {
                  value: "shipped",
                  label: text.statuses[OrderStatus.OrderShipped] || "En camino",
                },
                {
                  value: "pending",
                  label: text.statuses[OrderStatus.Pending] || "Pendientes",
                },
              ]}
              periodValue={periodFilter}
              onPeriodChange={setPeriodFilter}
              periodOptions={[
                { value: "30d", label: t.orderTracking.last30Days },
                { value: "3m", label: t.orderTracking.last3Months },
                { value: "2026", label: "2026" },
                { value: "2025", label: "2025" },
              ]}
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder={text.searchPlaceholder}
            />

            <div className="mx-auto max-w-7xl space-y-8">
              {filteredOrders.length === 0 ? (
                <div className="py-16 text-center text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  {text.notFound}
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <BuyerOrderCard
                    key={order.id}
                    order={order}
                    labels={buyerOrderLabels}
                    commonLabels={{
                      quantity: t.common.quantity,
                      total: t.common.total,
                    }}
                    isCheckingOut={isCheckingOut}
                    checkingOutSubOrderId={checkingOutSubOrderId}
                    awaitingPaymentSubOrderIds={awaitingPaymentSubOrderIds}
                    expandedPickupIds={expandedPickupIds}
                    onPayNow={handlePayNow}
                    onDownloadInvoice={handleDownloadInvoice}
                    onTrackShipment={handleTrackShipment}
                    onReorder={handleReorder}
                    onTogglePickupAddress={togglePickupAddress}
                  />
                ))
              )}
            </div>
          </>
        )}
      </main>

      <Dialog
        open={!!activePayment}
        onOpenChange={(open) => {
          if (!open) setActivePayment(null)
        }}
      >
        <DialogContent className="max-w-2xl overflow-hidden p-0">
          <DialogHeader className="border-b border-border px-6 pb-4 pt-6">
            <DialogTitle className="font-display text-2xl uppercase tracking-widest">
              {t.checkout.paymentMethod}
            </DialogTitle>
          </DialogHeader>
          <div
            id="order-tracking-conekta-container"
            className="w-full bg-background"
            style={{ height: "700px" }}
          />
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  )
}

export default OrderTracking
